// React
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// User Actions
import {
  updateProfile,
  updateDemographics,
  getUserDetails,
  exportUserData,
  deleteAccount,
} from "../../actions/userActions";

// Page Data
import { demographicsOutstanding } from "../../constants/demographicOptions";

// Internal Components
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";

// Stylesheet
import "./profile.css";
import SEO from "../../components/seo/SEO";

export default function ProfileDataScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State: User Login (Source of Truth for Auth)
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo, loading: loginLoading } = userLogin;

  const userExport = useSelector((state) => state.userExport);
  const {
    loading: exportLoading,
    success: exportSuccess,
    error: exportError,
  } = userExport;

  const userDelete = useSelector((state) => state.userDelete);
  const {
    loading: deleteLoading,
    success: deleteSuccess,
    error: deleteError,
  } = userDelete;

  const userUpdate = useSelector((state) => state.userUpdate);
  const { loading: updateLoading, success } = userUpdate;

  // Local State
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // 1. Auth Check & Data Synchronization
  useEffect(() => {
    if (!userInfo && !loginLoading) {
      return;
    } else if (userInfo) {
      if (!name || success) {
        setName(userInfo.name);
        setEmail(userInfo.email);
      }
    }
  }, [dispatch, navigate, userInfo, loginLoading, success, name]);

  useEffect(() => {
    if (!userInfo && !loginLoading) {
      return;
    }
  }, [dispatch, userInfo, loginLoading]);

  useEffect(() => {
    if (success) {
      setShowSuccess(true);
      setMessage(null);

      setPassword("");
      setConfirmPassword("");

      dispatch(getUserDetails());

      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const isLoading = loginLoading || updateLoading;

  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (deleteSuccess) {
      navigate("/"); // Redirect to homepage or login screen
    }
  }, [deleteSuccess, navigate]);

  const handleExport = () => {
    dispatch(exportUserData());
  };

  const handleDelete = (e) => {
    e.preventDefault();
    if (password) {
      dispatch(deleteAccount(password));
    }
  };

  return (
    <>
      <SEO
        title="My Profile"
        description="Explore your journal log, update your details and look at some frequently asked questions."
        canonicalUrl={"https://nowasteddays.io/my-account/menu"}
      />
      <Header variant="light" />
      <main className="mainProfileScreen">
        <div className="data-control-container">
          <h1 className="page-title">Data & Privacy Control</h1>
          {/* <p>Control over data is not an afterthought on NoWastedDays. It is an absolutely essential part of the experience. If you want to do your own analysis off-platform, export...</p>
<p>If it isn't working for you, or you have capture a good chunk of value but don't think you'll need it for a whiel.</p> */}

          {exportError && (
            <div className="error-message" style={{ color: "red" }}>
              {exportError}
            </div>
          )}
          {deleteError && (
            <div className="error-message" style={{ color: "red" }}>
              {deleteError}
            </div>
          )}
          {exportSuccess && (
            <div className="success-message" style={{ color: "#004a22" }}>
              Data exported successfully! Check your downloads.
            </div>
          )}

          {/* About You Card — what is held beyond the journal itself, and the
              means to change or erase it. Stating it plainly here is the point:
              the ask is only defensible if the undo is easy to find. */}
          <div className="control-card">
            <h2 className="card-title">About You</h2>
            <p className="body-text">
              {demographicsOutstanding(userInfo && userInfo.demographic)
                ? "You haven't told us anything about yourself, and you don't have to. If you'd like to, it helps us see who we're serving and who we're missing."
                : "We hold your answers to a handful of optional questions — things like age band, country and working life. Never your name, never your journal."}
            </p>
            <button
              className="action-button"
              onClick={() => navigate("/about-you")}
            >
              {demographicsOutstanding(userInfo && userInfo.demographic)
                ? "Answer the questions"
                : "View or change my answers"}
            </button>
            {!demographicsOutstanding(userInfo && userInfo.demographic) && (
              <button
                className="action-button"
                onClick={() => dispatch(updateDemographics({ clear: true }))}
              >
                Erase my answers
              </button>
            )}
          </div>

          {/* Export Card */}
          <div className="control-card">
            <h2 className="card-title">Export Your Data</h2>
            <p className="body-text">
              Download a copy of all your quizzes, debriefs, and weekly
              summaries.
            </p>
            <button
              className="action-button"
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? "Processing..." : "Request Data Export"}
            </button>
          </div>

          {/* Delete Card */}
          <div className="control-card danger-zone">
            <h2 className="card-title">Delete Account & Data</h2>
            <p className="body-text">
              Permanently delete your account and wipe all associated data from
              our servers.
              <strong> This action cannot be undone.</strong>
            </p>

            {!showConfirm ? (
              <button
                className="action-button danger-button"
                onClick={() => setShowConfirm(true)}
              >
                Initiate Deletion
              </button>
            ) : (
              <form className="deletion-form" onSubmit={handleDelete}>
                <div className="divider"></div>
                <label className="input-label" htmlFor="password">
                  Please enter your password to confirm:
                </label>
                <input
                  type="password"
                  id="password"
                  className="standard-input"
                  placeholder="Enter password..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={deleteLoading}
                />
                <div className="button-group">
                  <button
                    type="button"
                    className="action-button cancel-button"
                    onClick={() => setShowConfirm(false)}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="action-button confirm-delete-button"
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? "Deleting..." : "Permanently Delete"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
      <TestFooter />
    </>
  );
}
